import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Empty, Tooltip, Popover, Input, Checkbox, Button } from 'antd';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import Spinner from '../Spinner';
import * as Styled from './styledComponents';

const ITEMS_PER_PAGE = 50;

/**
 * Renders a searchable checkbox list of unique column values for filtering.
 * Defined outside TanStackUserTable so it can use useState without hook-order issues.
 */
const FilterPopoverContent = ({ column, facetedRows }) => {
  const [search, setSearch] = useState('');
  const currentFilter = column.getFilterValue() ?? [];

  // Columns with array-valued accessors (e.g. facilities) can provide a
  // custom extractor via columnDef.meta.getUniqueValues(rows) → string[].
  const getUniqueValues = column.columnDef.meta?.getUniqueValues;

  const options = getUniqueValues
    ? getUniqueValues(facetedRows || [])
        .filter(label => label.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.localeCompare(b))
        .map(label => ({ label }))
    : Array.from(column.getFacetedUniqueValues().entries())
        .map(([value]) => ({
          label: value != null ? String(value) : 'N/A'
        }))
        .filter(({ label }) =>
          label.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div style={{ minWidth: 180, maxWidth: 260, padding: '0 8px' }}>
      <Input
        size='small'
        placeholder='Search...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {options.length === 0 ? (
          <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, padding: '4px 0' }}>
            No options
          </div>
        ) : (
          options.map(({ label }) => (
            <div key={label} style={{ padding: '3px 0' }}>
              <Checkbox
                checked={currentFilter.includes(label)}
                onChange={e => {
                  const newValues = e.target.checked
                    ? [...currentFilter, label]
                    : currentFilter.filter(v => v !== label);
                  column.setFilterValue(newValues.length > 0 ? newValues : undefined);
                }}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
              </Checkbox>
            </div>
          ))
        )}
      </div>
      {currentFilter.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f2f5', marginTop: 8, paddingTop: 6, textAlign: 'right' }}>
          <Button
            type='link'
            size='small'
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

const TanStackUserTable = ({
  // Data props
  data,
  columns,
  loading,

  // Search
  searchValue,

  // Filters (e.g., facility filter)
  filterConfig,

  // Custom filter function
  customFilterFn,

  // Session storage key for filter persistence (optional)
  storageKey,

  // Trigger to clear all column filters from parent
  clearFiltersTrigger,

  // Column visibility (object keyed by column id, value boolean)
  columnVisibility
}) => {
  const [theme] = useSelector(state => [state.themeReducer]);

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const container = tableContainerRef.current;
    const tableEl = tableRef.current;
    if (!container) return;
    let rafId;
    const updateWidth = () => {
      rafId = requestAnimationFrame(() => {
        if (topScrollRef.current?.firstChild) {
          topScrollRef.current.firstChild.style.width = `${container.scrollWidth}px`;
        }
      });
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    if (tableEl) observer.observe(tableEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [data, columnVisibility]);

  const handleTopScroll = useCallback(() => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, []);

  // Local state for TanStack Table
  const [sortingState, setSortingState] = useState([{ id: 'fullName', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [filterVisible, setFilterVisible] = useState({});
  const [columnSizing, setColumnSizing] = useState({});

  // Load persisted column filters from session storage
  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      setColumnFilters(saved ? JSON.parse(saved) : []);
    } catch {
      setColumnFilters([]);
    }
  }, [storageKey]);

  // Persist column filters to session storage whenever they change
  useEffect(() => {
    if (!storageKey) return;
    if (columnFilters.length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(columnFilters));
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [columnFilters, storageKey]);

  // Column ordering state
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const NON_DRAGGABLE_COLUMNS = ['avatar', 'actions'];

  // Column drag-and-drop handlers
  const handleColumnDragStart = useCallback((e, headerId) => {
    setDraggedColumnId(headerId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleColumnDragOver = useCallback((e, headerId) => {
    e.preventDefault();
    if (headerId !== draggedColumnId) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverColumnId(headerId);
    }
  }, [draggedColumnId]);

  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleColumnDrop = useCallback((e, targetId) => {
    e.preventDefault();
    if (!draggedColumnId || draggedColumnId === targetId) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }
    setColumnOrder(prev => {
      const currentOrder = prev.length > 0
        ? [...prev]
        : columns.map(c => c.id);
      const fromIdx = currentOrder.indexOf(draggedColumnId);
      const toIdx = currentOrder.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      currentOrder.splice(fromIdx, 1);
      currentOrder.splice(toIdx, 0, draggedColumnId);
      return currentOrder;
    });
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, [draggedColumnId, columns]);

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, []);

  // Sync search value with global filter
  useEffect(() => {
    setGlobalFilter(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    if (clearFiltersTrigger) {
      setColumnFilters([]);
    }
  }, [clearFiltersTrigger]);

  // Apply custom filters if provided (e.g., facility filter)
  const filteredData = useMemo(() => {
    if (!data) return [];

    if (customFilterFn) {
      return data.filter(customFilterFn);
    }

    return data;
  }, [data, customFilterFn]);

  // TanStack Table instance
  const table = useReactTable({
    data: filteredData,
    columns,

    // Client-side pagination
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: ITEMS_PER_PAGE,
        pageIndex: 0
      }
    },

    // Client-side sorting
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,

    // Client-side filtering
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',

    // Column resizing
    enableColumnResizing: true,
    columnResizeMode: 'onChange',

    // State
    state: {
      sorting: sortingState,
      globalFilter,
      columnFilters,
      columnOrder,
      columnSizing,
      ...(columnVisibility ? { columnVisibility } : {})
    },

    // Callbacks
    onSortingChange: setSortingState,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,

    // Core models
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),

    // Row identification
    getRowId: row => row.id
  });

  // Empty state check
  const isEmpty = !filteredData || filteredData.length === 0;

  return (
    <ThemeProvider theme={theme}>
      <Styled.TableContainer style={{ position: 'relative' }} ref={tableContainerRef} onScroll={handleTableScroll}>
        <Styled.TopScrollBar ref={topScrollRef} onScroll={handleTopScroll}>
          <div style={{ height: 1 }} />
        </Styled.TopScrollBar>
        {/* Loading overlay */}
        {loading && (
          <Styled.LoadingOverlay>
            <Spinner />
          </Styled.LoadingOverlay>
        )}

        {isEmpty && !loading ? (
          <Styled.EmptyState>
            <Empty description='No data available' />
          </Styled.EmptyState>
        ) : (
          <>
            <Styled.StyledTable
              ref={tableRef}
              style={{
                width: table.getTotalSize(),
                minWidth: '100%',
                tableLayout: 'fixed',
                transition: table.getState().columnSizingInfo.isResizingColumn
                  ? 'none'
                  : 'width 0.15s ease'
              }}
            >
              <Styled.StyledTableHead>
                {table.getHeaderGroups().map(headerGroup => (
                  <Styled.StyledTableRow key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const isDraggable = !NON_DRAGGABLE_COLUMNS.includes(header.id);
                      return (
                      <Styled.DraggableHeader
                        key={header.id}
                        style={{ width: header.getSize() }}
                        isDragging={draggedColumnId === header.id}
                        isOver={dragOverColumnId === header.id}
                        onDragOver={isDraggable ? e => handleColumnDragOver(e, header.id) : undefined}
                        onDragLeave={isDraggable ? handleColumnDragLeave : undefined}
                        onDrop={isDraggable ? e => handleColumnDrop(e, header.id) : undefined}
                      >
                        {header.isPlaceholder ? null : (
                          <Styled.HeaderContent
                            draggable={isDraggable}
                            onClick={
                              header.column.getCanSort() && !draggedColumnId
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            canSort={header.column.getCanSort()}
                            onDragStart={isDraggable ? e => handleColumnDragStart(e, header.id) : undefined}
                            onDragEnd={isDraggable ? handleColumnDragEnd : undefined}
                          >
                            {/* 1. Sort indicator */}
                            {header.column.getCanSort() && (
                              <Styled.SortIndicator
                                direction={header.column.getIsSorted()}
                              >
                                <span className='sort-arrow-up'>▲</span>
                                <span className='sort-arrow-down'>▼</span>
                              </Styled.SortIndicator>
                            )}

                            {/* 2. Column name */}
                            <span style={{ flex: 1 }}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>

                            {/* 3. Filter icon */}
                            {header.column.getCanFilter() && (
                              <span onClick={e => e.stopPropagation()}>
                                <Popover
                                  content={
                                    <FilterPopoverContent
                                      column={header.column}
                                      facetedRows={header.column.getFacetedRowModel().rows}
                                    />
                                  }
                                  trigger='click'
                                  visible={filterVisible[header.id] || false}
                                  onVisibleChange={visible =>
                                    setFilterVisible(prev => ({
                                      ...prev,
                                      [header.id]: visible
                                    }))
                                  }
                                  placement='bottomRight'
                                >
                                  <FilterOutlined
                                    style={{
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      color: header.column.getFilterValue()
                                        ? '#47a4ea'
                                        : 'rgba(0,0,0,0.25)'
                                    }}
                                  />
                                </Popover>
                              </span>
                            )}
                          </Styled.HeaderContent>
                        )}

                          {/* Resize handle */}
                          {header.column.getCanResize() && (
                            <Styled.ResizeHandle
                              onMouseDown={e => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={header.getResizeHandler()}
                              isResizing={header.column.getIsResizing()}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                      </Styled.DraggableHeader>
                      );
                    })}
                  </Styled.StyledTableRow>
                ))}
              </Styled.StyledTableHead>

              <Styled.StyledTableBody>
                {table.getRowModel().rows.map(row => (
                  <Styled.StyledTableRow key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Styled.StyledTableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Styled.StyledTableCell>
                    ))}
                  </Styled.StyledTableRow>
                ))}
              </Styled.StyledTableBody>
            </Styled.StyledTable>

            {/* Pagination Controls */}
            <Styled.PaginationContainer>
              <Styled.PaginationInfo>
                Showing {table.getState().pagination.pageIndex * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * ITEMS_PER_PAGE,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                of {table.getFilteredRowModel().rows.length} results
                {(globalFilter || filteredData.length !== data?.length) &&
                  ` (filtered from ${data?.length || 0} total)`}
              </Styled.PaginationInfo>

              <Styled.PaginationControls>
                <Tooltip title='First Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleLeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.setPageIndex(0)}
                    aria-label='First Page'
                  />
                </Tooltip>

                <Tooltip title='Previous Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<LeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.previousPage()}
                    aria-label='Previous Page'
                  />
                </Tooltip>

                <span>
                  Page {table.getState().pagination.pageIndex + 1} of{' '}
                  {table.getPageCount() || 1}
                </span>

                <Tooltip title='Next Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<RightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.nextPage()}
                    aria-label='Next Page'
                  />
                </Tooltip>

                <Tooltip title='Last Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleRightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    aria-label='Last Page'
                  />
                </Tooltip>
              </Styled.PaginationControls>
            </Styled.PaginationContainer>
          </>
        )}
      </Styled.TableContainer>
    </ThemeProvider>
  );
};

export default TanStackUserTable;
